/**
 * Hook for Meta Ads Configuration Management
 * Gerencia múltiplos perfis Meta Ads com React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { useToast } from '@/hooks/use-toast';
import type { MetaAdsConfig, MetaAdsConfigInput, TokenStatus } from '@/types/meta-ads-config';
import { getTokenStatus as getTokenStatusHelper } from '@/types/meta-ads-config';
import {
  getAllMetaAdsConfigs,
  saveMetaAdsConfig,
  deleteMetaAdsConfig,
  toggleMetaAdsConfigActive,
  getTokenStatusAPI,
} from '@/lib/api/meta-ads-config';
import { withTimeout } from '@/lib/query-client';

export function useMetaAdsConfig() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  // Query: Buscar TODAS as configurações
  const {
    data: configs = [],
    isLoading,
    error,
    refetch,
  } = useQuery<MetaAdsConfig[]>({
    queryKey: ['meta-ads-configs'],
    queryFn: withTimeout(() => getAllMetaAdsConfigs()),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Mutation: Salvar configuração (cria ou atualiza)
  const saveMutation = useMutation({
    mutationFn: async (data: MetaAdsConfigInput) => {
      if (!user?.id) {
        throw new Error('Usuário não autenticado');
      }
      return saveMetaAdsConfig(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-ads-configs'] });
      queryClient.invalidateQueries({ queryKey: ['meta-ads-token-status'] });
      toast({
        title: 'Sucesso',
        description: 'Configuração salva com sucesso',
      });
    },
    onError: (error: Error) => {
      console.error('Erro ao salvar configuração:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível salvar a configuração',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Deletar configuração
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return deleteMetaAdsConfig(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta-ads-configs'] });
      queryClient.invalidateQueries({ queryKey: ['meta-ads-token-status'] });
      toast({
        title: 'Sucesso',
        description: 'Perfil removido com sucesso',
      });
    },
    onError: (error: Error) => {
      console.error('Erro ao deletar configuração:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível remover o perfil',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Ativar/desativar perfil
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, ativo }: { id: string; ativo: boolean }) => {
      return toggleMetaAdsConfigActive(id, ativo);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meta-ads-configs'] });
      toast({
        title: 'Sucesso',
        description: variables.ativo ? 'Perfil ativado' : 'Perfil desativado',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível alterar o status',
        variant: 'destructive',
      });
    },
  });

  // Compatibilidade: primeiro config ativo (para TokenExpirationWarning etc)
  const primaryConfig = configs.find(c => (c as MetaAdsConfig & { ativo?: boolean }).ativo !== false) ?? configs[0] ?? null;
  const tokenStatus: TokenStatus | null = primaryConfig
    ? getTokenStatusHelper(primaryConfig.token_data_validade)
    : null;

  return {
    // Data
    configs,
    config: primaryConfig, // compatibilidade com código existente
    tokenStatus,

    // States
    isLoading,
    error,
    isSaving: saveMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isToggling: toggleActiveMutation.isPending,

    // Actions
    save: saveMutation.mutateAsync,
    deleteConfig: deleteMutation.mutateAsync,
    toggleActive: toggleActiveMutation.mutateAsync,
    refetch,
  };
}

/**
 * Hook simplificado apenas para status do token
 * Usado pelo TokenExpirationWarning banner
 */
export function useTokenStatus() {
  const { data: tokenStatus, isLoading } = useQuery({
    queryKey: ['meta-ads-token-status'],
    queryFn: withTimeout(() => getTokenStatusAPI()),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    tokenStatus,
    isLoading,
  };
}
